import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  Box, Button, Typography, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, CircularProgress, MenuItem
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, PictureAsPdf, Email, CheckCircle, Cancel, Warning, Payment, AccountBalance } from '@mui/icons-material';
import { salesService } from '../../services/sales.service';
import postingService from '../../services/posting.service';

const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [emailDialog, setEmailDialog] = useState<{ open: boolean; invoiceId: number | null; customerName: string; customerEmail: string }>({
    open: false, invoiceId: null, customerName: '', customerEmail: ''
  });
  const [emailForm, setEmailForm] = useState({ email: '', name: '', message: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [paymentDialog, setPaymentDialog] = useState<{ open: boolean; invoiceId: number | null; balance: number; currencyCode: string }>({
    open: false, invoiceId: null, balance: 0, currencyCode: 'USD'
  });
  const [paymentForm, setPaymentForm] = useState({ amount: 0, paymentDate: new Date().toISOString().split('T')[0], paymentMethod: 'BANK_TRANSFER', reference: '' });
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { loadInvoices(); }, []);

  const handlePostToLedger = async (id: number) => {
    if (!window.confirm(t('posting.post_confirm'))) return;
    try {
      const res = await postingService.postSalesInvoice(id);
      alert(res.data.message);
      loadInvoices();
    } catch (err: any) { alert(err.response?.data?.message || 'Error posting to ledger'); }
  };

  const loadInvoices = async () => {
    try { const data = await salesService.getAllInvoices(); setInvoices(data as any); }
    catch (error) { console.error('Error loading invoices:', error); }
    finally { setLoading(false); }
  };

  const handleDownloadPdf = async (id: number) => {
    setPdfLoading(id);
    try { await salesService.downloadInvoicePdf(id); }
    catch (error: any) { alert(error.response?.data?.message || 'Error generating PDF'); }
    finally { setPdfLoading(null); }
  };

  const handleOpenEmailDialog = (row: any) => {
    setEmailForm({ email: row.customerEmail || '', name: row.customerName || '', message: '' });
    setEmailDialog({ open: true, invoiceId: row.id, customerName: row.customerName, customerEmail: row.customerEmail || '' });
    setEmailResult(null);
  };

  const handleSendEmail = async () => {
    if (!emailDialog.invoiceId) return;
    setEmailSending(true); setEmailResult(null);
    try {
      const result = await salesService.sendInvoiceEmail(emailDialog.invoiceId, {
        email: emailForm.email || undefined, name: emailForm.name || undefined, message: emailForm.message || undefined
      });
      setEmailResult({ type: 'success', message: result.message });
      setTimeout(() => setEmailDialog({ ...emailDialog, open: false }), 2000);
    } catch (error: any) {
      setEmailResult({ type: 'error', message: error.response?.data?.message || 'Failed to send email.' });
    } finally { setEmailSending(false); }
  };

  const handleStatusChange = async (id: number, status: string, label: string) => {
    if (!window.confirm(`${label} this invoice?`)) return;
    try {
      await salesService.updateInvoiceStatus(id, status);
      loadInvoices();
    } catch (error: any) { alert(error.response?.data?.message || 'Error updating status'); }
  };

  const handleOpenPayment = (row: any) => {
    const balance = Number(row.totalAmount) - Number(row.paidAmount);
    setPaymentForm({ amount: balance, paymentDate: new Date().toISOString().split('T')[0], paymentMethod: 'BANK_TRANSFER', reference: '' });
    setPaymentDialog({ open: true, invoiceId: row.id, balance, currencyCode: row.currencyCode || 'USD' });
  };

  const handleRecordPayment = async () => {
    if (!paymentDialog.invoiceId) return;
    try {
      await salesService.recordPayment(paymentDialog.invoiceId, paymentForm);
      setPaymentDialog({ ...paymentDialog, open: false });
      loadInvoices();
    } catch (error: any) { alert(error.response?.data?.message || 'Error recording payment'); }
  };

  const getStatusColor = (status: string): "default" | "warning" | "success" | "error" | "info" => {
    const colors: Record<string, any> = {
      'DRAFT': 'default', 'PENDING': 'warning', 'PAID': 'success',
      'PARTIAL': 'info', 'OVERDUE': 'error', 'CANCELLED': 'default'
    };
    return colors[status] || 'default';
  };

  const columns: GridColDef[] = [
    { field: 'invoiceNumber', headerName: t('invoices.invoice_number'), width: 200 },
    { field: 'invoiceDate', headerName: t('common.date'), width: 100, renderCell: (p: GridRenderCellParams) => new Date(p.value).toLocaleDateString() },
    { field: 'dueDate', headerName: t('invoices.due_date'), width: 100, renderCell: (p: GridRenderCellParams) => new Date(p.value).toLocaleDateString() },
    { field: 'customerName', headerName: t('sales_orders.customer'), flex: 1, minWidth: 150 },
    { field: 'status', headerName: t('common.status'), width: 110, renderCell: (p: GridRenderCellParams) => <Chip label={p.value} color={getStatusColor(p.value as string)} size="small" /> },
    {
      field: 'totalAmount', headerName: t('common.total'), width: 120, align: 'right', headerAlign: 'right',
      renderCell: (p: GridRenderCellParams) => `${Number(p.value).toFixed(2)} ${p.row.currencyCode || 'USD'}`
    },
    {
      field: 'balanceAmount', headerName: t('common.balance'), width: 120, align: 'right', headerAlign: 'right',
      renderCell: (p: GridRenderCellParams) => {
        const val = Number(p.value); const curr = p.row.currencyCode || 'USD';
        return <span style={{ color: val > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 'bold' }}>{val.toFixed(2)} {curr}</span>;
      }
    },
    {
      field: 'actions', headerName: t('common.actions'), width: 250, sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const s = params.row.status;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={t('invoices.download_pdf')}>
              <IconButton size="small" color="error" onClick={() => handleDownloadPdf(params.row.id)} disabled={pdfLoading === params.row.id}>
                {pdfLoading === params.row.id ? <CircularProgress size={16} /> : <PictureAsPdf fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('invoices.send_email')}>
              <IconButton size="small" color="primary" onClick={() => handleOpenEmailDialog(params.row)}>
                <Email fontSize="small" />
              </IconButton>
            </Tooltip>
            {s !== 'DRAFT' && s !== 'CANCELLED' && (
              <Tooltip title={t('posting.post_to_ledger')}>
                <IconButton size="small" sx={{ color: '#6a1b9a' }} onClick={() => handlePostToLedger(params.row.id)}>
                  <AccountBalance fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(s === 'PENDING' || s === 'PARTIAL' || s === 'OVERDUE') && (
              <Tooltip title={t('invoices.record_payment')}>
                <IconButton size="small" color="success" onClick={() => handleOpenPayment(params.row)}>
                  <Payment fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(s === 'PENDING' || s === 'PARTIAL') && (
              <Tooltip title={t('invoices.mark_paid')}>
                <IconButton size="small" sx={{ color: '#2e7d32' }} onClick={() => handleStatusChange(params.row.id, 'PAID', 'Mark as Paid')}>
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {s === 'PENDING' && (
              <Tooltip title={t('invoices.mark_overdue')}>
                <IconButton size="small" color="warning" onClick={() => handleStatusChange(params.row.id, 'OVERDUE', 'Mark as Overdue')}>
                  <Warning fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(s === 'PENDING' || s === 'OVERDUE') && (
              <Tooltip title="Cancel">
                <IconButton size="small" color="error" onClick={() => handleStatusChange(params.row.id, 'CANCELLED', 'Cancel')}>
                  <Cancel fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('invoices.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/invoices/new')}>{t('invoices.new')}</Button>
      </Box>
      <DataGrid rows={invoices} columns={columns} loading={loading} autoHeight
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50]} disableRowSelectionOnClick />

      {/* Email Dialog */}
      <Dialog open={emailDialog.open} onClose={() => setEmailDialog({ ...emailDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoices.send_email')}</DialogTitle>
        <DialogContent>
          {emailResult && <Alert severity={emailResult.type} sx={{ mb: 2, mt: 1 }}>{emailResult.message}</Alert>}
          <TextField fullWidth label={t('invoices.email_recipient')} value={emailForm.email} onChange={e => setEmailForm({ ...emailForm, email: e.target.value })}
            sx={{ mt: 2 }} type="email" required helperText={emailDialog.customerEmail ? `Customer email: ${emailDialog.customerEmail}` : 'No customer email on file'} />
          <TextField fullWidth label={t('invoices.email_name')} value={emailForm.name} onChange={e => setEmailForm({ ...emailForm, name: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label={t('invoices.email_message')} value={emailForm.message} onChange={e => setEmailForm({ ...emailForm, message: e.target.value })}
            sx={{ mt: 2 }} multiline rows={3} placeholder="Leave empty for default message" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog({ ...emailDialog, open: false })}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSendEmail} disabled={emailSending || !emailForm.email}
            startIcon={emailSending ? <CircularProgress size={18} /> : <Email />}>{emailSending ? 'Sending...' : 'Send Invoice'}</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onClose={() => setPaymentDialog({ ...paymentDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('invoices.record_payment')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>Balance due: {paymentDialog.balance.toFixed(2)} {paymentDialog.currencyCode}</Alert>
          <TextField fullWidth required label={t('invoices.payment_amount')} type="number" value={paymentForm.amount}
            onChange={e => setPaymentForm({ ...paymentForm, amount: Number(e.target.value) })}
            inputProps={{ min: 0.01, step: 0.01, max: paymentDialog.balance }} sx={{ mt: 1 }} />
          <TextField fullWidth required type="date" label={t('invoices.payment_date')} value={paymentForm.paymentDate}
            onChange={e => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
            InputLabelProps={{ shrink: true }} sx={{ mt: 2 }} />
          <TextField fullWidth select label={t('invoices.payment_method')} value={paymentForm.paymentMethod}
            onChange={e => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })} sx={{ mt: 2 }}>
            <MenuItem value="BANK_TRANSFER">{t('invoices.bank_transfer')}</MenuItem>
            <MenuItem value="CASH">{t('invoices.cash')}</MenuItem>
            <MenuItem value="CHECK">{t('invoices.check')}</MenuItem>
            <MenuItem value="CREDIT_CARD">{t('invoices.credit_card')}</MenuItem>
            <MenuItem value="OTHER">{t('invoices.other')}</MenuItem>
          </TextField>
          <TextField fullWidth label={t('invoices.payment_reference')} value={paymentForm.reference}
            onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog({ ...paymentDialog, open: false })}>{t('common.cancel')}</Button>
          <Button variant="contained" color="success" onClick={handleRecordPayment}
            disabled={paymentForm.amount <= 0} startIcon={<Payment />}>Record Payment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;
